import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const LiverFunctionPage = () => {
  const navigate = useNavigate();
  const [activeFunction, setActiveFunction] = useState(0);

  const functions = [
    {
      id: 0,
      title: '代謝機能',
      icon: 'ri-flask-line',
      color: 'from-amber-500 to-orange-500',
      gradientStart: '#f59e0b',
      gradientEnd: '#f97316',
      bgColor: 'bg-amber-50',
      description: '栄養素を体が使える形に変換',
      details: [
        '糖質をグリコーゲンとして貯蔵',
        'タンパク質の合成と分解',
        '脂質の代謝と胆汁酸の生成',
        'ビタミンやミネラルの貯蔵'
      ]
    },
    {
      id: 1,
      title: '解毒機能',
      icon: 'ri-shield-check-line',
      color: 'from-green-500 to-emerald-500',
      gradientStart: '#22c55e',
      gradientEnd: '#10b981',
      bgColor: 'bg-green-50',
      description: '有害物質を無害化して排出',
      details: [
        'アルコールの分解',
        '薬物の代謝と解毒',
        'アンモニアを尿素に変換',
        '老廃物の処理と排出'
      ]
    },
    {
      id: 2,
      title: '胆汁生成',
      icon: 'ri-drop-line',
      color: 'from-yellow-500 to-amber-500',
      gradientStart: '#eab308',
      gradientEnd: '#f59e0b',
      bgColor: 'bg-yellow-50',
      description: '脂肪の消化を助ける胆汁を生成',
      details: [
        '1日約500-1000mlの胆汁を生成',
        '脂肪の乳化と吸収を促進',
        '脂溶性ビタミンの吸収を助ける',
        '老廃物の排出経路'
      ]
    },
    {
      id: 3,
      title: '貯蔵機能',
      icon: 'ri-database-2-line',
      color: 'from-blue-500 to-cyan-500',
      gradientStart: '#3b82f6',
      gradientEnd: '#06b6d4',
      bgColor: 'bg-blue-50',
      description: 'エネルギーと栄養素を貯蔵',
      details: [
        'グリコーゲンとしてエネルギーを貯蔵',
        'ビタミンA、D、E、K、B12を貯蔵',
        '鉄分や銅などのミネラルを貯蔵',
        '必要時にエネルギーを供給'
      ]
    },
    {
      id: 4,
      title: '合成機能',
      icon: 'ri-test-tube-line',
      color: 'from-purple-500 to-pink-500',
      gradientStart: '#a855f7',
      gradientEnd: '#ec4899',
      bgColor: 'bg-purple-50',
      description: '生命維持に必要な物質を合成',
      details: [
        'アルブミンなどの血漿タンパク質',
        '血液凝固因子の生成',
        'コレステロールの合成',
        '免疫に関わる物質の生成'
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFunction((prev) => (prev + 1) % functions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [functions.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
          >
            ホームに戻る
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              肝臓の<span className="text-amber-600">5つの役割</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              肝臓は「沈黙の臓器」と呼ばれ、500以上の機能を持つ重要な器官です
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Liver Animation */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFunction}
                  initial={{ opacity: 0, scale: 0.9, rotateY: 90 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  exit={{ opacity: 0, scale: 0.9, rotateY: -90 }}
                  transition={{ duration: 0.6, type: 'spring' }}
                  className="relative"
                >
                  {/* Liver SVG */}
                  <div className="relative w-full max-w-md mx-auto aspect-square flex items-center justify-center">
                    <svg viewBox="0 0 400 400" className="w-full h-full">
                      <defs>
                        <linearGradient id={`liverGradient${activeFunction}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor={functions[activeFunction].gradientStart} />
                          <stop offset="100%" stopColor={functions[activeFunction].gradientEnd} />
                        </linearGradient>
                        <filter id="glow">
                          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                          <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                          </feMerge>
                        </filter>
                      </defs>
                      
                      {/* Liver Shape */}
                      <motion.path
                        d="M 200 80 
                           C 240 80, 280 100, 300 130
                           C 320 160, 330 200, 320 240
                           C 310 280, 280 310, 240 320
                           C 200 330, 160 320, 130 300
                           C 100 280, 80 240, 85 200
                           C 90 160, 110 120, 140 100
                           C 170 80, 190 80, 200 80 Z"
                        fill={`url(#liverGradient${activeFunction})`}
                        filter="url(#glow)"
                        initial={{ scale: 0.95 }}
                        animate={{ scale: [0.95, 1, 0.95] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      />

                      {/* Function Icons */}
                      {activeFunction === 0 && (
                        <>
                          <motion.circle cx="150" cy="150" r="8" fill="#fbbf24" opacity="0.8"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                          />
                          <motion.circle cx="200" cy="180" r="10" fill="#f59e0b" opacity="0.8"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                          />
                          <motion.circle cx="250" cy="160" r="7" fill="#fbbf24" opacity="0.8"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
                          />
                        </>
                      )}

                      {activeFunction === 1 && (
                        <>
                          <motion.path d="M 180 140 L 190 150 L 170 160" stroke="#10b981" strokeWidth="3" fill="none"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1, repeat: Infinity }}
                          />
                          <motion.circle cx="220" cy="200" r="6" fill="#10b981"
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                        </>
                      )}

                      {activeFunction === 2 && (
                        <>
                          <motion.path
                            d="M 200 200 Q 220 220, 240 240"
                            stroke="#eab308"
                            strokeWidth="4"
                            fill="none"
                            strokeLinecap="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: [0, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                          <motion.circle cx="240" cy="240" r="8" fill="#eab308"
                            animate={{ scale: [0, 1.2, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                        </>
                      )}

                      {activeFunction === 3 && (
                        <>
                          <motion.rect x="170" y="170" width="20" height="20" rx="3" fill="#3b82f6" opacity="0.7"
                            animate={{ y: [170, 165, 170] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          />
                          <motion.rect x="210" y="190" width="18" height="18" rx="3" fill="#06b6d4" opacity="0.7"
                            animate={{ y: [190, 185, 190] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                          />
                        </>
                      )}

                      {activeFunction === 4 && (
                        <>
                          <motion.circle cx="180" cy="160" r="5" fill="#a855f7"
                            animate={{ scale: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                          <motion.circle cx="220" cy="180" r="5" fill="#ec4899"
                            animate={{ scale: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          />
                        </>
                      )}
                    </svg>

                    {/* Function Label */}
                    <motion.div
                      key={`label-${activeFunction}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-center"
                    >
                      <div className={`inline-flex items-center gap-2 px-6 py-3 ${functions[activeFunction].bgColor} rounded-full shadow-lg`}>
                        <i className={`${functions[activeFunction].icon} text-2xl`}></i>
                        <span className="text-lg font-bold text-gray-900">{functions[activeFunction].title}</span>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right: Function Details */}
            <div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFunction}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <div className={`inline-flex items-center gap-3 px-4 py-2 ${functions[activeFunction].bgColor} rounded-full`}>
                    <i className={`${functions[activeFunction].icon} text-3xl`}></i>
                    <h2 className="text-3xl font-bold text-gray-900">{functions[activeFunction].title}</h2>
                  </div>

                  <p className="text-xl text-gray-700 font-medium">
                    {functions[activeFunction].description}
                  </p>

                  <ul className="space-y-4">
                    {functions[activeFunction].details.map((detail, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex-shrink-0 mt-1">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-base text-gray-700 leading-relaxed">{detail}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>

              {/* Progress Indicators */}
              <div className="flex gap-2 mt-8">
                {functions.map((func, index) => (
                  <button
                    key={func.id}
                    onClick={() => setActiveFunction(index)}
                    className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      index === activeFunction ? 'w-12 bg-gradient-to-r ' + func.color : 'w-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">肝臓の驚くべき能力</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl"
            >
              <div className="text-4xl font-bold text-amber-600 mb-2">500+</div>
              <div className="text-sm text-gray-700">以上の機能</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl"
            >
              <div className="text-4xl font-bold text-green-600 mb-2">1.2-1.5kg</div>
              <div className="text-sm text-gray-700">成人の肝臓の重さ</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl"
            >
              <div className="text-4xl font-bold text-blue-600 mb-2">70%</div>
              <div className="text-sm text-gray-700">切除しても再生可能</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl"
            >
              <div className="text-4xl font-bold text-purple-600 mb-2">24時間</div>
              <div className="text-sm text-gray-700">休まず働き続ける</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-green-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              あなたの肝臓の状態をチェックしませんか？
            </h2>
            <p className="text-xl text-white/90 mb-8">
              血液検査のデータから、肝臓の健康状態を確認できます
            </p>
            <motion.button
              onClick={() => navigate('/check')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-12 py-5 bg-white text-blue-600 text-lg font-bold rounded-full hover:bg-gray-50 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
            >
              無料でチェックする
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold">Liver Health Check</span>
          </div>
          <p className="text-sm text-gray-400 mb-6">
            本サービスは医療行為・診断を行うものではありません。健康情報の提供を目的としています。
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              利用規約
            </button>
            <button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              プライバシーポリシー
            </button>
            <button onClick={() => navigate('/disclaimer')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              免責事項
            </button>
            <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              お問い合わせ
            </button>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-sm text-gray-500">
            © 2024 Liver Health Check. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LiverFunctionPage;
